import { useState, useRef, useEffect } from 'react'
import { LogOut, ChevronDown, KeyRound } from 'lucide-react'
import { useAuth } from '../auth/AuthContext'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { ChangePasswordModal } from '../auth/ChangePasswordModal'

interface NavbarProps {
  showUserIcon?: boolean
  logoSize?: 'small' | 'medium' | 'large'
  className?: string
}

export const Navbar = ({
  showUserIcon = true,
  logoSize = 'medium',
  className = '',
}: NavbarProps) => {
  const { user, logout, isAuthenticated } = useAuth()
  const [showDropdown, setShowDropdown] = useState(false)
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  const getLogoSize = () => {
    switch (logoSize) {
      case 'small':
        return 'h-16 w-auto'
      case 'large':
        return 'h-24 w-auto'
      default:
        return 'h-20 w-auto'
    }
  }

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleLogout = () => {
    logout()
    setShowDropdown(false)
    toast.success('Sesión cerrada correctamente')
    navigate('/login')
  }

  const handleChangePassword = () => {
    setShowDropdown(false)
    setShowChangePasswordModal(true)
  }

  const getUserDisplayName = () => {
    if (!user) return 'Usuario'

    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`
    } else if (user.firstName) {
      return user.firstName
    } else if (user.email) {
      return user.email.split('@')[0]
    }

    return 'Usuario'
  }

  return (
    <>
      <div
        className={`h-24 flex items-center justify-between px-6 ${className}`}
        style={{
          background:
            'linear-gradient(90deg, #1A85C3 0%, #225EAC 85%, #225EAC 100%)',
        }}
      >
        {/* Logo */}
        <img
          src="/assets/olr-logo.png"
          alt="OLR Leasing"
          className={`filter brightness-0 invert ${getLogoSize()}`}
        />

        {/* User section */}
        {showUserIcon && isAuthenticated && (
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className="flex items-center space-x-2 text-white hover:bg-white/10 rounded-lg px-3 py-2 transition-colors"
            >
              <img
                src="/assets/user-icon.svg"
                alt="OLR Leasing"
                className={`size-5`}
              />
              <span className="text-sm font-medium hidden sm:block">
                {getUserDisplayName()}
              </span>
              <ChevronDown
                size={16}
                className={`transition-transform ${showDropdown ? 'rotate-180' : ''}`}
              />
            </button>

            {/* Dropdown menu */}
            {showDropdown && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                <div className="px-4 py-2 border-b border-gray-100">
                  <p className="text-sm font-medium text-gray-900">
                    {getUserDisplayName()}
                  </p>
                  <p className="text-xs text-gray-500">{user?.email}</p>
                </div>

                {user?.userType !== 'GUARANTOR' && (
                  <button
                    onClick={handleChangePassword}
                    className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                  >
                    <KeyRound size={16} />
                    <span>Cambiar contraseña</span>
                  </button>
                )}

                <button
                  onClick={handleLogout}
                  className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <LogOut size={16} />
                  <span>Cerrar sesión</span>
                </button>
              </div>
            )}
          </div>
        )}

        {/* Simple user icon for non-authenticated users */}
        {showUserIcon && !isAuthenticated && (
          <img
            src="/assets/user-icon.svg"
            alt="OLR Leasing"
            className={`size-6`}
          />
        )}
      </div>

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
      />
    </>
  )
}
